import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { decryptFromBackend } from "../../../utils/urlDecryptor";
import { useAuthStore } from "../../../stores/authStores";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import ChatLoadingScreen from "./ChatLoadingScreen";
import ChatMessage from "./ChatMessage";
import ConsultationReminderModal from "./ConsultationReminderModal";
import FeedbackScreen from "./FeedbackScreen";
import PatientCard from "./PatientCard";

import { useChatMessages } from "./hooks/useChatMessages";
import { useKeyboardOffset } from "./hooks/useKeyboardOffset";
import { useTeleconsultation } from "./hooks/useTeleconsultation";
import { useZegoChat } from "./hooks/useZegoChat";
import { isSystemMessage } from "./utils/chatHelpers";
import MediaPreviewModal from "../../../components/MediaPreviewModal";

// ─── Styled Components ────────────────────────────────────────────────────────

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  height: 100vh;
  overflow: hidden;
  align-self: center;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 120px 16px 130px;
  overflow-y: auto;
  background-image: url("/images/bg-chat.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;

  & > :first-child {
    margin-top: auto;
  }
`;

const FlexSpacer = styled.div`
  flex: 1;
  min-height: 1px;
`;

const ChatInputContainer = styled.div`
  position: fixed;
  bottom: 0;
  bottom: env(safe-area-inset-bottom);
  background: #ffffff;
  padding: 0 0 calc(env(safe-area-inset-bottom) + 8px);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  z-index: 100;
  transform: translateY(0);
  transition: transform 0.2s ease;
  width: 100%;
`;

const AttachmentWarningBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #fff6e7;
  border-top: 1px solid #f2d39b;
  color: #8a5a00;
  font-size: 12px;
`;

const RetryButton = styled.button`
  border: none;
  border-radius: 999px;
  background: #ed1c24;
  color: #fff;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
`;

const RefreshArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  cursor: pointer;
  color: #999;
`;

const RefreshIcon = styled.div`
  font-size: 28px;
  color: #aaa;
  animation: spin 1s linear infinite;
  @keyframes spin { 
    from { transform: rotate(0deg); } 
    to { transform: rotate(-360deg); }
  }
`;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
`;

const SkeletonBubble = styled.div<{ isRight?: boolean }>`
  width: ${() => 120 + Math.random() * 80}px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  align-self: ${({ isRight }) => (isRight ? 'flex-end' : 'flex-start')};
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// ─── Main Component ───────────────────────────────────────────────────────────

const Chat: React.FC = () => {
  const { conversationID, token: urlToken } = useParams<{
    conversationID: string;
    token: string;
  }>();

  const effectiveToken = useMemo(() => {
    if (urlToken) return urlToken;
    const stored = conversationID ? localStorage.getItem(`teleconsult_token_${conversationID}`) : null;
    return stored || '';
  }, [urlToken, conversationID]);

  const decryptedToken = useMemo(() => {
    if (!effectiveToken) return "";
    try {
      return decryptFromBackend(effectiveToken);
    } catch (err) {
      console.error("Failed to decrypt token:", err);
      return "";
    }
  }, [effectiveToken]);

  const [previewMedia, setPreviewMedia] = React.useState<{
    url: string;
    fileName?: string;
    type?: "image" | "file" | "video" | "audio";
  } | null>(null);
  const refreshAttemptRef = React.useRef(0);

  // ─── Hooks ────────────────────────────────────────────────────────────────

  const {
    teleconsultationData,
    loadError,
    retryLoad,
    isExpired,
    isClosed,
    isEndedByDoctor,
    isEndedBySystem,
    showReminder,
    setShowReminder,
    showRating,
    setShowRating,
    setIsEndedByDoctor,
    setIsEndedBySystem,
  } = useTeleconsultation(conversationID, decryptedToken);

  const { isLoggedIn, isGroupReady, userId, userDokter, groupId } =
    useZegoChat(teleconsultationData);

  const {
    messages,
    replyTo,
    setReplyTo,
    handleSend,
    handleSendMedia,
    loading,
    refreshMessages,
    syncNow,
    failedAttachmentCount,
    attachmentSyncWarning,
    retryFailedAttachments,
  } =
    useChatMessages({
      isLoggedIn,
      isGroupReady,
      isActive: !isClosed && !isEndedByDoctor && !isEndedBySystem,
      groupId,
      userId,
      userDokter,
      conversationID,
      decryptedToken,
    });

  const keyboardOffset = useKeyboardOffset();

  // ─── Listen for consultation end event ───────────────────────────────────
  // useChatMessages dispatches 'consultation:ended' when it detects end|{id}

  const navigate = useNavigate();
  useEffect(() => {
    console.log('status teleconsult', teleconsultationData?.teleconsultation_status)
    const handleConsultationEnded = () => {
      if (teleconsultationData?.teleconsultation_status !== "ongoing") {
        setIsEndedByDoctor(true);
        setIsEndedBySystem(true);
        setShowRating(true);
      }
    };
    window.addEventListener("consultation:ended", handleConsultationEnded);
    return () =>
      window.removeEventListener("consultation:ended", handleConsultationEnded);
  }, [setShowRating]);

  // ─── Refs ─────────────────────────────────────────────────────────────────

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLDivElement | null>(null);
  const messageRefsMap = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [highlightedMessageID, setHighlightedMessageID] = React.useState<
    string | null
  >(null);
  const isConsultationActive = !isExpired && !isEndedByDoctor && !isEndedBySystem;

  // ─── Scroll to bottom ─────────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, keyboardOffset, scrollToBottom]);

  // ─── Reply quote click ────────────────────────────────────────────────────

  const handleReplyQuoteClick = (messageID: string) => {
    setHighlightedMessageID(messageID);
    const messageElement = messageRefsMap.current[messageID];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => setHighlightedMessageID(null), 2000);
  };

  // ─── Loading state ────────────────────────────────────────────────────────

  if (!teleconsultationData) {
    return <ChatLoadingScreen loadError={loadError} onRetry={retryLoad} />;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <ChatContainer>
      <ChatHeader
        doctorAvatar={
          teleconsultationData.practitioner_avatar_url ||
          "https://m.linksehat.com/assets/img/users/female-avatar.png"
        }
        doctorName={teleconsultationData.practitioner_name || "Dokter"}
        doctorSpecialty={
          teleconsultationData.practitioner_speciality || "Spesialis Umum"
        }
        consultationMedia={teleconsultationData.teleconsultation_type || "CHAT"}
        startTime={teleconsultationData.teleconsultation_start_time}
        duration={teleconsultationData.teleconsultation_duration_timeout}
        isExpired={isExpired}
        consultationId={teleconsultationData.teleconsultation_id || ""}
        token={decryptedToken}
        serverTime={teleconsultationData.server_time}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "12px 0",
          boxShadow: "2px 2px 2px rgba(0,0,0,0.1)",
          zIndex: 100,
        }}
      >
        <PatientCard
          name={teleconsultationData.patient_name || "Nama Pasien"}
          insurance={teleconsultationData.patient_insurance || "Insurance"}
        />
      </div>

      <MessagesContainer ref={messagesContainerRef}>
        <FlexSpacer />
        {messages.length === 0 && !loading && (
          <RefreshArea onClick={async () => {
            refreshAttemptRef.current += 1;

            if (refreshAttemptRef.current >= 3) {
              window.location.reload();
              return;
            }
            await refreshMessages();
          }}>
            <span style={{ fontSize: 13, color: '#999' }}>
              {refreshAttemptRef.current === 0
                ? 'Tap untuk memuat pesan'
                : refreshAttemptRef.current === 1
                ? 'Memuat ulang... tap lagi jika belum muncul'
                : `Mencoba lagi (${refreshAttemptRef.current}/3)...`
              }
            </span>
            <RefreshIcon>↺</RefreshIcon>
          </RefreshArea>
        )}

        {loading && (
          <SkeletonWrapper>
            {[1, 2, 3].map((i) => (
              <SkeletonBubble key={i} isRight={i % 2 === 0} />
            ))}
          </SkeletonWrapper>
        )}
        
        {messages
          .filter(
            (msg) =>
              typeof msg.message === "string" && !isSystemMessage(msg.message),
          )
          .map((msg) => (
            <ChatMessage
              key={`${msg.messageID}-${msg.timestamp}`}
              {...msg}
              onReply={() => setReplyTo(msg as any)}
              onMediaClick={(url, fileName, type) =>   // ← tambah prop ini
                setPreviewMedia({ url, fileName, type })
              }
              doctorName={teleconsultationData.practitioner_name || "Dokter"}
              doctorSpecialty={
                teleconsultationData.practitioner_speciality || "Spesialis Umum"
              }
              avatar={teleconsultationData.practitioner_avatar_url}
              userDokter={userDokter}
              patientName={teleconsultationData.patient_name}
              ref={(element: any) => {
                if (element) messageRefsMap.current[msg.messageID] = element;
              }}
              isHighlighted={highlightedMessageID === msg.messageID}
              onReplyQuoteClick={handleReplyQuoteClick}
              replyTo={msg.replyTo}
            />
          ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {isConsultationActive ? (
        <ChatInputContainer ref={chatInputRef} style={{ bottom: keyboardOffset }}>
          {failedAttachmentCount > 0 && attachmentSyncWarning && (
            <AttachmentWarningBanner>
              <span>
                {attachmentSyncWarning} {failedAttachmentCount} lampiran perlu disinkronkan ulang.
              </span>
              <RetryButton type="button" onClick={retryFailedAttachments}>
                Sync Ulang
              </RetryButton>
            </AttachmentWarningBanner>
          )}
          <ChatInput
            onSend={handleSend}
            onSendMedia={handleSendMedia}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            doctorName={teleconsultationData.practitioner_name || 'Dokter'}
            isDoctor={false}
          />
        </ChatInputContainer>
      ) : (
        <div style={{
          position: 'fixed',
          bottom: 0,
          width: '100%',
          backgroundColor: '#f5f5f5',
          padding: '16px',
          textAlign: 'center',
          borderTop: '1px solid #e0e0e0',
          zIndex: 100,
          boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.05)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            color: '#666',
            fontSize: '14px',
            fontWeight: 500,
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="#999"/>
            </svg>
            <span>Konsultasi telah berakhir</span>
          </div>
        </div>
      )}

      <ConsultationReminderModal
        visible={showReminder}
        onClose={() => setShowReminder(false)}
      />

      <FeedbackScreen
        doctorId={teleconsultationData.practitioner_id || ""}
        teleconsultId={teleconsultationData.teleconsultation_id || ""}
        teleconsultationCode={teleconsultationData.teleconsultation_code || ""}
        patientId={teleconsultationData.patient_id || ""}
        token={decryptedToken}
        visible={showRating}
        onClose={() => {
          setShowRating(false);
          syncNow();
        }}
      />
      {previewMedia && (
        <MediaPreviewModal
          url={previewMedia.url}
          fileName={previewMedia.fileName}
          type={previewMedia.type}
          onClose={() => setPreviewMedia(null)}
        />
      )}
    </ChatContainer>
  );
};

export default Chat;
